import React, { useMemo, useState } from 'react';

import image from '../../shared/assets/png/error404.png';
import {
  getProjectByID,
  getProjectAdditionalDocs,
  shareAdditionalDocuments,
  uploadProjectAdditionalDocs,
  uploadFilesToS3
} from '../services/api/implementation/impl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { FaFileAlt } from 'react-icons/fa';
import { MdShare } from 'react-icons/md';
import { IoMdClose } from 'react-icons/io';
import { FiUpload } from 'react-icons/fi';
import {
  FileText,
  FileImage,
  FileSpreadsheet,
  FileArchive,
  FileType2,
  FileCode2
} from 'lucide-react';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import Loader from '../../shared/components/ui/Loader';

export const ProjectDocument = () => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Multi-selection state
  const [selectedForSharing, setSelectedForSharing] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileNames, setFileNames] = useState({}); // Store custom names for each file
  const [isUploading, setIsUploading] = useState(false);

  const getDocumentUniqueKey = (document) => {
    if (!document) return '';
    if (document.id !== undefined && document.id !== null) return `id-${document.id}`;
    if (document.url) return `url-${document.url}`;
    return `name-${document.name || 'untitled'}`;
  };

  /* 
      Data fetching
    */
  const { data: project, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project'],
    queryFn: () => getProjectByID(projectId)
  });

  const queryClient = useQueryClient();

  const {
    data: projectAdditionalDocs,
    refetch: refetchAdditionalDocs,
    isFetching
  } = useQuery({
    queryKey: ['projectAdditionalDocs', projectId],
    queryFn: () => getProjectAdditionalDocs(projectId)
  });

  console.log({ project });

  const getFileIcon = (fileName, size = 18) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return <FileType2 size={size} className="text-red-500" />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
      case 'svg':
        return <FileImage size={size} className="text-sky-500" />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <FileSpreadsheet size={size} className="text-emerald-600" />;
      case 'zip':
      case 'rar':
      case '7z':
        return <FileArchive size={size} className="text-amber-600" />;
      case 'doc':
      case 'docx':
      case 'txt':
        return <FileText size={size} className="text-indigo-600" />;
      case 'json':
      case 'xml':
        return <FileCode2 size={size} className="text-violet-600" />;
      default:
        return <FileText size={size} className="text-slate-500" />; // Default icon for other file types
    }
  };

  const truncateFileName = (fileName, maxLength = 15) => {
    if (!fileName || typeof fileName !== 'string') return 'Untitled document';
    if (fileName.length <= maxLength) return fileName;
    if (!fileName.includes('.')) return `${fileName.slice(0, maxLength - 3)}...`;
    const extension = fileName.split('.').pop();
    const nameWithoutExtension = fileName.slice(0, -(extension.length + 1));
    return `${nameWithoutExtension.slice(0, maxLength - 3)}...${extension}`;
  };

  // Multi-selection functions
  const toggleDocumentSelection = (document) => {
    const targetKey = getDocumentUniqueKey(document);
    setSelectedForSharing((prev) => {
      const isSelected = prev.some((doc) => getDocumentUniqueKey(doc) === targetKey);
      if (isSelected) {
        return prev.filter((doc) => getDocumentUniqueKey(doc) !== targetKey);
      } else {
        return [...prev, document];
      }
    });
  };

  const isDocumentSelected = (document) => {
    const targetKey = getDocumentUniqueKey(document);
    return selectedForSharing.some((doc) => getDocumentUniqueKey(doc) === targetKey);
  };

  const clearSelection = () => {
    setSelectedForSharing([]);
  };

  // Share functionality
  const handleShareClick = (document) => {
    setSelectedDocuments([document]);
    setEmailBody(`Please find the document below:\n\n${document.name}\n${document.url}`);
    setShowShareModal(true);
  };

  const handleBulkShare = () => {
    if (selectedForSharing.length === 0) {
      toast.error('Please select at least one document to share');
      return;
    }

    setSelectedDocuments(selectedForSharing);
    setEmailBody(
      `Please find the documents below:\n\n${selectedForSharing.map((doc) => `${doc.name}\n${doc.url}`).join('\n\n')}`
    );
    setShowShareModal(true);
  };

  const handleShareMultiple = (documents) => {
    setSelectedDocuments(documents);
    setEmailBody(
      `Please find the documents below:\n\n${documents.map((doc) => `${doc.name}\n${doc.url}`).join('\n\n')}`
    );
    setShowShareModal(true);
  };

  const { mutate: handleSendEmail, isLoading: isSendingEmail } = useMutation({
    mutationFn: async () => {
      // Validate required fields
      if (!emailRecipients.trim()) {
        throw new Error('Please enter email recipients');
      }

      // Parse email recipients (comma or space separated)
      const emails = emailRecipients
        .split(/[,\s]+/)
        .map((email) => email.trim())
        .filter((email) => email);

      // Extract document URLs
      const docs = selectedDocuments.map((doc) => doc.url);

      const payload = {
        emails,
        docs,
        body: emailBody || ''
      };

      return await shareAdditionalDocuments(payload);
    },
    onSuccess: () => {
      toast.success('Documents shared successfully!');
      setShowShareModal(false);

      // Reset form and clear selections
      setSelectedDocuments([]);
      setSelectedForSharing([]);
      setEmailRecipients('');
      setEmailBody('');
    },
    onError: (error) => {
      console.error('Error sharing documents:', error);
      toast.error(error?.message || error?.response?.data?.message || 'Failed to share documents');
    }
  });

  const handleCopyToClipboard = () => {
    try {
      // Copy only the email body
      navigator.clipboard
        .writeText(emailBody)
        .then(() => {
          toast.success('Email body copied to clipboard!');
        })
        .catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = emailBody;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          toast.success('Email body copied to clipboard!');
        });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Error copying to clipboard. Please try again.');
    }
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setSelectedDocuments([]);
    setEmailRecipients('');
    setEmailBody('');
  };

  // Upload functionality
  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    // Clear the input so the same files can be selected again if needed
    event.target.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    // Also remove the custom name for this file
    setFileNames((prev) => {
      const newNames = { ...prev };
      delete newNames[index];
      return newNames;
    });
  };

  const handleFileNameChange = (index, customName) => {
    setFileNames((prev) => ({
      ...prev,
      [index]: customName
    }));
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setSelectedFiles([]);
    setFileNames({});
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFiles([]);
    setFileNames({});
  };

  const { mutate: handleUploadFiles } = useMutation({
    mutationFn: async ({ files, customNames }) => {
      // Step 1: Upload files to S3 first
      const uploadedFiles = await uploadFilesToS3(files);

      // Step 2: Create the payload with custom names and S3 URLs
      const docs = uploadedFiles.map((file, index) => {
        // Get the file extension from the original file
        const originalFileName = files[index].name;
        const fileExtension = originalFileName.substring(originalFileName.lastIndexOf('.'));

        // Append extension to custom name
        const nameWithExtension = customNames[index]
          ? `${customNames[index]}${fileExtension}`
          : file.name;

        return {
          name: nameWithExtension,
          url: file.url
        };
      });

      const payload = {
        project_id: parseInt(projectId),
        docs: docs
      };

      // Step 3: Send the docs with URLs to the project API
      return await uploadProjectAdditionalDocs(payload);
    },
    onSuccess: () => {
      toast.success('Files uploaded successfully!');
      setShowUploadModal(false);
      setSelectedFiles([]);
      setFileNames({});
      refetchAdditionalDocs();
      queryClient.invalidateQueries(['projectAdditionalDocs', projectId]);
      setIsUploading(false);
    },
    onError: (error) => {
      console.error('Upload error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to upload files');
    }
  });

  const handleUploadSubmit = () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    // Validate that all files have custom names
    const missingNames = selectedFiles.some((file, index) => !fileNames[index]?.trim());
    if (missingNames) {
      toast.error('Please provide a name for all documents');
      return;
    }

    setIsUploading(true);
    handleUploadFiles({ files: selectedFiles, customNames: fileNames });
  };

  let projectImages = project?.projectImages?.map((item) => item);
  let buildingFiles = project?.buildings?.map((item) => [
    ...item.additionalDocuments,
    ...item.layoutPlans,
    ...item.developmentOrders,
    ...item.uploadLetterheads,
    ...item.googleMapImages
  ]);
  let googleImage = project?.googleImage?.map((item) => item);
  let brochures = project?.brochures?.map((item) => item);
  let tncp_approved_plan = project?.tncp_approved_plan?.map((item) => item);
  let tncp_sanctioned_layout_plan = project?.tncp_sanctioned_layout_plan?.map((item) => item);
  let projectSpecification = project?.projectSpecification.map((item) => item);

  const allProjectFiles = buildingFiles && buildingFiles.length > 0 ? buildingFiles[0] : [];
  const tableSections = [
    { title: 'Project Images', docs: projectImages || [] },
    { title: 'Project Files', docs: allProjectFiles },
    { title: 'Google Images', docs: googleImage || [] },
    { title: 'Brochures', docs: brochures || [] },
    { title: 'TNCP Approved Plan', docs: tncp_approved_plan || [] },
    { title: 'TNCP Sanctioned Layout Plan', docs: tncp_sanctioned_layout_plan || [] },
    { title: 'Project Specification', docs: projectSpecification || [] },
    { title: 'Additional Documents', docs: projectAdditionalDocs || [] }
  ];
  const unifiedDocuments = tableSections.flatMap((section) =>
    section.docs.map((doc) => ({
      ...doc,
      section: section.title
    }))
  );
  const filteredDocuments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return unifiedDocuments;

    return unifiedDocuments.filter((doc) => {
      const name = (doc?.name || '').toLowerCase();
      const section = (doc?.section || '').toLowerCase();
      const fileType = (doc?.name?.split('.')?.pop() || '').toLowerCase();
      return name.includes(query) || section.includes(query) || fileType.includes(query);
    });
  }, [searchQuery, unifiedDocuments]);

  return (
    <>
      <div className="w-full flex flex-col pl-5 pr-5 bg-gray-100 pb-10 overflow-hidden mt-5">
        <Loader isVisible={isProjectLoading || isUploading || isFetching} />
        {/* Bulk Share Controls */}
        {selectedForSharing.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-blue-800 font-medium">
                  {selectedForSharing.length} document{selectedForSharing.length > 1 ? 's' : ''}{' '}
                  selected
                </span>
                <button
                  onClick={clearSelection}
                  className="text-blue-600 hover:text-blue-800 text-sm underline">
                  Clear Selection
                </button>
              </div>
              <Button
                onClick={handleBulkShare}
                className="bg-[#346C75] hover:bg-[#2c5b63] text-white">
                Share Selected ({selectedForSharing.length})
              </Button>
            </div>
          </div>
        )}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-base font-semibold text-gray-800">All Project Documents</h3>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, category, or type..."
                className="w-72 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={openUploadModal}
                type="button"
                className="border border-customBlue bg-customBlue text-white py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center gap-2">
                <FiUpload size={16} />
                Upload Files
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-medium w-16">Select</th>
                  <th className="px-4 py-3 text-left font-medium">Document</th>
                  <th className="px-4 py-3 text-left font-medium w-52">Category</th>
                  <th className="px-4 py-3 text-left font-medium w-40">Type</th>
                  <th className="px-4 py-3 text-left font-medium w-44">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.length > 0 ? (
                  filteredDocuments.map((item, i) => (
                    <tr
                      key={`${item.section}-${item.url || item.name}-${i}`}
                      className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={isDocumentSelected(item)}
                          onChange={() => toggleDocumentSelection(item)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center justify-center">
                            {getFileIcon(item.name, 18)}
                          </span>
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            download={item.name}
                            className="text-blue-700 hover:text-blue-900 hover:underline font-medium">
                            {truncateFileName(item.name, 32)}
                          </a>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{item.section}</td>
                      <td className="px-4 py-3 text-gray-600 uppercase text-xs tracking-wide">
                        {item.name?.split('.')?.pop() || 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer"
                            download={item.name}
                            className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100">
                            Open
                          </a>
                          <button
                            onClick={() => handleShareClick(item)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#346C75] text-white hover:bg-[#2c5b63]"
                            title="Share this document">
                            <MdShare size={14} />
                            Share
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                      No matching documents found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <Modal isDirty confirmOnClose width={50} name="Share Document" onClose={closeShareModal}>
          <div className="flex flex-col gap-4">
            {/* Selected Documents Preview */}
            {selectedDocuments.length > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Selected {selectedDocuments.length === 1 ? 'document' : 'documents'}:
                </p>
                <div className="space-y-1">
                  {selectedDocuments.map((doc, index) => (
                    <p key={index} className="text-sm text-gray-600">
                      • {doc.name}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Email Form */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Recipients *
              </label>
              <input
                type="email"
                placeholder="Enter email addresses (comma-separated)"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Body</label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={6}
                placeholder="Email body content..."
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" onClick={closeShareModal} displayType="secondary">
                Cancel
              </Button>
              <Button type="button" onClick={handleCopyToClipboard} displayType="secondary">
                Copy to Clipboard
              </Button>
              <Button
                type="button"
                onClick={handleSendEmail}
                disabled={!emailRecipients || isSendingEmail}>
                {isSendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Upload Additional Documents"
          onClose={closeUploadModal}>
          <div className="flex flex-col gap-4 relative max-h-[48rem]">
            {/* File Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Files (Multiple files allowed)
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xlsx,.xls"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, TXT, XLSX, XLS
              </p>
            </div>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    Selected files ({selectedFiles.length}):
                  </p>
                  <button
                    onClick={() => setSelectedFiles([])}
                    className="text-red-600 hover:text-red-800 text-sm underline">
                    Clear All
                  </button>
                </div>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-white p-3 rounded border">
                      <div className="flex-shrink-0">{getFileIcon(file.name, 20)}</div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">
                          Original file:{' '}
                          <span className="font-medium text-gray-700">{file.name}</span>
                        </p>
                        <input
                          type="text"
                          placeholder="Enter custom document name *"
                          value={fileNames[index] || ''}
                          onChange={(e) => handleFileNameChange(index, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                          required
                        />
                      </div>
                      <button
                        onClick={() => removeSelectedFile(index)}
                        className="text-red-500 hover:text-red-700 flex-shrink-0">
                        <IoMdClose size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" onClick={closeUploadModal} displayType="secondary">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUploadSubmit}
                disabled={selectedFiles.length === 0 || isUploading}
                className="bg-green-600 hover:bg-green-700 text-white">
                {isUploading
                  ? 'Uploading...'
                  : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
